import type { MouseEvent, ReactNode } from 'react';

import { useEffect, useState } from 'react';

import useClassy from '@core/hooks/useClassy';
import ErrorState from '@ui/ErrorState';

import classes from './RenderErrorFallback.module.scss';

export type RenderEngine = 'mdxish' | 'mdx' | 'rdmd';

interface Props {
  /** Source markdown / MDX content that the renderer was asked to render. */
  body?: string;
  /** Which renderer produced the failure. Used in the debug log. */
  engine?: RenderEngine;
  /** The thrown error, when one was captured (parse-phase or ErrorBoundary). */
  error?: Error | null;
  message?: ReactNode;
  title?: ReactNode;
}

const ENGINE_LABEL: Record<RenderEngine, string> = {
  mdxish: 'MDXish',
  mdx: 'MDX (RMDX)',
  rdmd: 'RDMD (legacy)',
};

function buildDebugLog(engine: RenderEngine | undefined, error: Error | null | undefined, body: string | undefined) {
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const errorBlock = error ? error.stack || `${error.name}: ${error.message}` : '(no error captured)';

  return [
    `Renderer: ${engine ? ENGINE_LABEL[engine] : 'unknown'}`,
    `Time:     ${new Date().toISOString()}`,
    `URL:      ${url}`,
    `UA:       ${ua}`,
    '',
    '--- Error ---',
    errorBlock,
    '',
    '--- Page content ---',
    body && body.length ? body : '(empty)',
  ].join('\n');
}

function RenderErrorFallback({ body, engine, error, message, title }: Props) {
  const bem = useClassy(classes, 'RenderErrorFallback');
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 2500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const handleIconClick = (event: MouseEvent<HTMLElement>) => {
    if (!event.shiftKey) return;
    const next = !revealed;
    setRevealed(next);

    if (!next) {
      setCopied(false);
      return;
    }

    const log = buildDebugLog(engine, error, body);
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(log)
        .then(() => setCopied(true))
        .catch(() => {
          // Clipboard write can fail (permissions, insecure context). Reveal still works.
        });
    }
  };

  return (
    <div className={bem('&')}>
      <ErrorState
        message={message ?? 'Page content is not renderable'}
        onIconClick={handleIconClick}
        title={title ?? 'Unable to render content'}
      />
      {revealed ? (
        <div className={bem('-debug')}>
          {copied ? <div className={bem('-copied')}>Debug log copied!</div> : null}
          <pre className={bem('-log')}>{buildDebugLog(engine, error, body)}</pre>
        </div>
      ) : null}
    </div>
  );
}

export default RenderErrorFallback;
